import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { AimOutlined, EnvironmentOutlined, SearchOutlined } from '@ant-design/icons';
import { locateBrowser, reverseMapAddress, searchMapPlaces } from '@/utils/amap';
import { coordinatePair, gcj02ToWgs84, positionFields, wgs84ToGcj02 } from '@/utils/coordinates';
import MapCanvas from './MapCanvas';
import './ActivityLocationPicker.scss';

const ActivityLocationPicker = ({ id, longitude, latitude, location, onChange, disabled = false, onBusyChange, feedback, guidance, defaultCurrentLocation = true }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const requestVersion = useRef(0);
  const searchTimer = useRef(null);
  const composing = useRef(false);
  const mounted = useRef(false);
  const latest = useRef({});
  latest.current = { open, longitude, latitude, location, onChange, onBusyChange, disabled, defaultCurrentLocation };
  const point = coordinatePair(longitude, latitude);
  const longitudeValue = longitude ?? null; const latitudeValue = latitude ?? null; const locationValue = location ?? '';
  const run = async (type, operation) => {
    clearTimeout(searchTimer.current);
    const version = ++requestVersion.current;
    const current = () => mounted.current && version === requestVersion.current;
    setBusy(type); setError('');
    try { await operation(current); }
    catch (err) { if (current()) setError(err.message || '位置操作失败，请重试'); }
    finally { if (current()) setBusy(''); }
  };
  const locate = automatic => run('locate', async current => {
    const position = await locateBrowser();
    if (!current()) return;
    if (!coordinatePair(position?.[0], position?.[1])) throw new Error('暂时无法获取有效位置，请重试或手动选点');
    setCurrentPosition(position);
    const fields = latest.current;
    if (automatic && (coordinatePair(fields.longitude, fields.latitude) || fields.location?.trim() || fields.disabled)) return;
    setQuery(''); setPlaces([]); setSearched(false); setSearching(false);
    fields.onChange({ ...positionFields(position), location: '当前位置' });
    if (!automatic && fields.open) close();
  });
  const locateRef = useRef(locate);
  locateRef.current = locate;
  useEffect(() => {
    clearTimeout(searchTimer.current);
    requestVersion.current += 1; setBusy('');
    if (disabled) setOpen(false);
  }, [longitudeValue, latitudeValue, locationValue, disabled]);
  useEffect(() => {
    mounted.current = true;
    const fields = latest.current;
    if (fields.defaultCurrentLocation || (!coordinatePair(fields.longitude, fields.latitude) && !fields.location?.trim())) locateRef.current(true);
    return () => { mounted.current = false; clearTimeout(searchTimer.current); requestVersion.current += 1; latest.current.onBusyChange?.(false); };
  }, []);
  useEffect(() => { latest.current.onBusyChange?.(open || Boolean(busy)); }, [open, busy]);

  const close = () => {
    clearTimeout(searchTimer.current);
    composing.current = false;
    requestVersion.current += 1;
    setOpen(false); setBusy(''); setError(''); setSearching(false);
  };
  const begin = () => {
    setQuery(''); setPlaces([]); setSearched(false); setSearching(false); setError(''); setOpen(true);
  };
  const search = value => {
    const text = (typeof value === 'string' ? value : query).trim();
    if (!text) return;
    setQuery(text); setPlaces([]); setSearched(false); setSearching(true);
    run('search', async current => {
      const result = await (currentPosition ? searchMapPlaces(text, currentPosition) : searchMapPlaces(text));
      if (current()) { setPlaces(result); setSearched(true); }
    });
  };
  const edit = value => {
    clearTimeout(searchTimer.current);
    requestVersion.current += 1;
    setQuery(value); setBusy(''); setError(''); setPlaces([]); setSearched(false); setSearching(true);
    if (value.trim() && !composing.current) searchTimer.current = setTimeout(() => search(value), 350);
  };
  const pick = (position, name = '') => run('address', async current => {
    let placeName = name;
    if (!placeName) {
      try { placeName = await reverseMapAddress(wgs84ToGcj02(position)); }
      catch { placeName = '已选活动地点'; }
    }
    if (current()) {
      latest.current.onChange({ ...positionFields(position), location: placeName || '已选活动地点' });
      close();
    }
  });

  return <div className="activity-location" onKeyDown={event => { if (open && event.key === 'Escape') { event.stopPropagation(); close(); } }}>
    <div className="activity-location-summary">
      <EnvironmentOutlined />
      <div aria-live="polite"><strong>{busy === 'locate' && !open ? '正在获取当前位置…' : location || (point ? '已选活动地点' : '尚未确认活动位置')}</strong>
        <p>{defaultCurrentLocation ? '默认使用当前位置，也可选择其他集合地点' : '可保留当前地点，也可选择其他集合地点'}</p></div>
      <Button disabled={disabled} onClick={open ? close : begin}>{open ? '收起地点选择' : '选择活动地点'}</Button>
    </div>
    {open && <section className={`activity-location-explorer${searching ? ' is-searching' : ''}`} aria-label="活动地点选择">
      <div className="activity-location-map">
        <MapCanvas longitude={point?.[0]} latitude={point?.[1]} currentPosition={currentPosition} onPick={pick} disabled={disabled} />
      </div>
      <div className="activity-location-search">
        <Input id={id} aria-label="活动地点" value={query} prefix={<SearchOutlined />} onChange={event => edit(event.target.value)} onFocus={() => setSearching(true)}
          onCompositionStart={() => { composing.current = true; clearTimeout(searchTimer.current); requestVersion.current += 1; setBusy(''); }}
          onCompositionEnd={event => { composing.current = false; edit(event.currentTarget.value); }}
          onPressEnter={event => { event.preventDefault(); if (!composing.current) search(query); }}
          placeholder="查找学校、场馆、集合地点" maxLength={200} allowClear disabled={disabled} />
      </div>
      <Button className="activity-location-locate" aria-label="使用当前位置" icon={<AimOutlined />} loading={busy === 'locate'} disabled={disabled || Boolean(busy)} onClick={() => locate(false)} />
      <div className="activity-location-panel">
        <div className="activity-location-handle" />
        {searching && <div className="activity-location-categories" aria-label="快捷搜索">
          {['学校', '体育场馆', '图书馆', '咖啡厅', '公园'].map(category => <Button key={category} disabled={disabled || Boolean(busy)} onClick={() => search(category)}>{category}</Button>)}
        </div>}
        {error && <Alert type="warning" showIcon title={error} />}
        {searching && <div className="activity-location-results" aria-live="polite">
          <h3>{busy === 'search' ? '正在搜索…' : searched ? '搜索结果' : '搜索活动地点'}</h3>
          {places.map(place => <button type="button" key={place.id} disabled={disabled || Boolean(busy)} onClick={() => pick(gcj02ToWgs84(place.point), place.name)}>
            <EnvironmentOutlined /><span><strong>{place.name}</strong><small>{place.address}</small></span><span className="activity-location-result-action">选择</span>
          </button>)}
          {!places.length && busy !== 'search' && <p>{searched ? '未找到相关地点，试试更完整的名称或在地图上选点。' : '输入名称搜索，或点击地图选择活动地点。'}</p>}
        </div>}
        {!searching && <div className="activity-location-preview" aria-live="polite">
          <EnvironmentOutlined /><div><strong>{busy === 'address' ? '正在获取地点名称…' : point ? location || '已选活动地点' : '点击地图选择活动地点'}</strong>
            <p>点击搜索结果或地图，即可设置活动地点</p></div>
        </div>}
      </div>
    </section>}
    {!open && error && <Alert type="warning" showIcon title={error} action={<Button disabled={disabled || Boolean(busy)} onClick={() => locate(false)}>重新定位</Button>} />}
    {!open && location?.trim() && !point && <Alert type="warning" showIcon title="活动地点尚未确认，请选择搜索结果、在地图上选点或使用当前位置。" />}
    <div className="activity-location-feedback">{feedback}{!open && guidance && <p>{guidance}</p>}</div>
  </div>;
};

export default ActivityLocationPicker;
